import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, FileSpreadsheet, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  downloadLoanDataExport,
  generateLoanDataExport,
  getLoanDataExportCatalog,
  getLoanDataExportDownload,
  LoanDataExportServiceError,
  preflightLoanDataExport,
  resolveLoanDataExportIds
} from '@/services/loanDataExportService';
import {
  clearLoanDataExportCandidates,
  isLoanDataExportPlaceholder,
  loanDataExportOwnerKey,
  reconcileLoanDataExportCandidates,
  redactLoanDataExportCandidates,
  removeLoanDataExportCandidate,
  setLoanDataExportSelectionOwner,
  useLoanDataExportSelection
} from '@/stores/loanDataExportSelectionStore';
import { useAuth } from '@/contexts/useAuth';
import { isExtendedUser } from '@/types/auth';
import { useLoanCenterScope } from '../prospects/loanScope';
import type {
  LoanDataExportCandidate,
  LoanDataExportExcludedLoan,
  LoanDataExportGenerationResult,
  LoanDataExportHistoryItem,
  LoanDataExportTemplate,
  LoanDataExportTemplateId
} from '@/types/loan-data-export';
import { formatCurrency } from '@/utils/formatCurrency';
import { ExportHistoryTable } from './ExportHistoryTable';
import { LoanSelectionPanel } from './LoanSelectionPanel';
import { TemplatePicker } from './TemplatePicker';

const NO_SELECTION: LoanDataExportCandidate[] = [];

function pluralized(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`;
}

function borrowerName(candidate: LoanDataExportCandidate): string {
  return [candidate.borrowerFirstName, candidate.borrowerLastName].filter(Boolean).join(' ') || 'Unknown borrower';
}

function serviceErrorIs(error: unknown, status: number, phrase: string): boolean {
  return error instanceof LoanDataExportServiceError
    && error.status === status
    && error.message.toLowerCase().includes(phrase);
}

function errorText(error: unknown, fallback: string): string {
  if (serviceErrorIs(error, 409, 'already being prepared')) {
    return 'This Excel is still being prepared. Try again in a moment.';
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

function describeExcluded(excluded: LoanDataExportExcludedLoan[]): string {
  const named = excluded.filter((loan) => loan.loanNumber);
  const parts = named.slice(0, 3).map((loan) => `${loan.loanNumber}: ${loan.reason}`);
  if (named.length > 3) {
    parts.push(`And ${(named.length - 3).toLocaleString()} more`);
  }
  const unnamed = excluded.length - named.length;
  if (unnamed > 0) {
    parts.push(`${pluralized(unnamed, 'loan')} you can no longer access`);
  }
  return `${parts.join('. ')}.`;
}

function downloadSummary(exportRow: LoanDataExportHistoryItem, templateName: string): string {
  const summary = `${templateName}, ${pluralized(exportRow.loan_count, 'loan')}.`;
  return exportRow.warning_count > 0
    ? `${summary} ${pluralized(exportRow.warning_count, 'required value')} empty in Encompass.`
    : summary;
}

export function LoanDataExportsPage() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const organizationId = isExtendedUser(user) ? user.organization_id || null : null;
  const ownerKey = loanDataExportOwnerKey(userId, organizationId);

  // Keyed by owner: when someone else signs in, every piece of this
  // workspace (search results, selection, work in progress) starts over.
  return (
    <LoanDataExportsWorkspace
      key={ownerKey ?? 'signed-out'}
      ownerKey={ownerKey}
      userId={userId}
      organizationId={organizationId}
    />
  );
}

interface WorkspaceProps {
  ownerKey: string | null;
  userId: string | null;
  organizationId: string | null;
}

function LoanDataExportsWorkspace({ ownerKey, userId, organizationId }: WorkspaceProps) {
  const loanScope = useLoanCenterScope();
  const [selectionReady, setSelectionReady] = useState(false);
  const storedSelection = useLoanDataExportSelection(ownerKey);
  const selection = selectionReady ? storedSelection : NO_SELECTION;
  const readySelected = useMemo(
    () => selection.filter((candidate) => !isLoanDataExportPlaceholder(candidate)),
    [selection]
  );
  const pendingCount = selection.length - readySelected.length;
  const selectedKey = useMemo(() => selection.map((candidate) => candidate.id).join(','), [selection]);

  const [templates, setTemplates] = useState<LoanDataExportTemplate[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<LoanDataExportTemplateId | null>(null);
  const [generating, setGenerating] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const mountedRef = useRef(true);
  const operationRef = useRef<number | null>(null);
  const operationSequence = useRef(0);
  const pendingRequest = useRef<{ key: string; id: string } | null>(null);
  const retryToasts = useRef(new Set<string | number>());

  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId) ?? null;

  // Runs before the first paint. The selection stays masked until it belongs
  // to this user and anything cached has been hidden pending the access check.
  useLayoutEffect(() => {
    setLoanDataExportSelectionOwner(userId, organizationId);
    redactLoanDataExportCandidates();
    setSelectionReady(true);
  }, [organizationId, userId]);

  useEffect(() => {
    mountedRef.current = true;
    const openToasts = retryToasts.current;
    return () => {
      mountedRef.current = false;
      openToasts.forEach((toastId) => toast.dismiss(toastId));
    };
  }, []);

  useEffect(() => {
    if (!selectionReady || loanScope.isLoading) {
      return;
    }
    if (!loanScope.canAccess || !ownerKey) {
      clearLoanDataExportCandidates();
      return;
    }

    const loanIds = selectedKey ? selectedKey.split(',') : [];
    if (loanIds.length === 0) {
      return;
    }

    let cancelled = false;
    const revalidate = async () => {
      try {
        const authorized = await resolveLoanDataExportIds(loanIds);
        if (cancelled) {
          return;
        }
        const { removed } = reconcileLoanDataExportCandidates(loanIds, authorized);
        if (removed > 0) {
          toast.info(`${pluralized(removed, 'selected loan')} removed`, {
            description: 'You no longer have access to them.'
          });
        }
      } catch {
        if (!cancelled) {
          redactLoanDataExportCandidates();
        }
      }
    };
    void revalidate();
    const interval = window.setInterval(() => {
      void revalidate();
    }, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    loanScope.canAccess,
    loanScope.isLoading,
    ownerKey,
    selectedKey,
    selectionReady
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadCatalog = async () => {
      setCatalogLoading(true);
      try {
        const catalog = await getLoanDataExportCatalog();
        if (!cancelled) {
          setTemplates(catalog);
          setSelectedTemplateId((current) => current ?? catalog[0]?.id ?? null);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : 'Unable to load data tapes');
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    };
    void loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  // One request ID per exact payload. An uncertain failure keeps it, so the
  // next click returns the export the server may already have finished.
  const requestIdFor = (payloadKey: string): string => {
    if (pendingRequest.current?.key !== payloadKey) {
      pendingRequest.current = { key: payloadKey, id: crypto.randomUUID() };
    }
    return pendingRequest.current.id;
  };

  const generateForPayload = async (
    templateId: LoanDataExportTemplateId,
    loanIds: string[]
  ): Promise<LoanDataExportGenerationResult> => {
    const payloadKey = `${templateId}:${[...loanIds].sort().join(',')}`;
    const request = () => generateLoanDataExport({
      loanIds,
      templateId,
      requestId: requestIdFor(payloadKey),
      acknowledgeWarnings: true
    });
    let generated: LoanDataExportGenerationResult;
    try {
      generated = await request();
    } catch (error) {
      // A retry is a new request made with whoever is signed in now, so it
      // only happens while this user's page is still open.
      if (!serviceErrorIs(error, 409, 'failed previously') || !mountedRef.current) {
        throw error;
      }
      // The server recorded this request ID as failed; start a new export once.
      pendingRequest.current = null;
      generated = await request();
    }
    pendingRequest.current = null;
    return generated;
  };

  const startDownload = async (exportRow: LoanDataExportHistoryItem, templateName: string): Promise<void> => {
    try {
      const latest = await getLoanDataExportDownload(exportRow.id);
      if (!mountedRef.current) {
        return;
      }
      const fileName = latest.export.file_name ?? exportRow.file_name;
      if (!fileName) {
        throw new Error('The Excel file is missing its name');
      }
      const saved = await downloadLoanDataExport(latest.download.signedUrl, fileName, {
        shouldContinue: () => mountedRef.current
      });
      if (saved) {
        toast.success('Download started', { description: downloadSummary(latest.export, templateName) });
      }
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      const toastId = toast.error('Your Excel is ready, but the download did not start', {
        description: `${errorText(error, 'The download failed')}. You can also download it from History.`,
        duration: 15_000,
        action: {
          label: 'Download again',
          onClick: () => {
            retryToasts.current.delete(toastId);
            void startDownload(exportRow, templateName);
          }
        }
      });
      retryToasts.current.add(toastId);
    }
  };

  const handleGenerate = async () => {
    if (operationRef.current !== null || !selectedTemplate || pendingCount > 0) {
      return;
    }
    const loanIds = readySelected.map((candidate) => candidate.id);
    if (loanIds.length === 0) {
      return;
    }
    const template = selectedTemplate;
    const token = operationSequence.current + 1;
    operationSequence.current = token;
    operationRef.current = token;
    setGenerating(true);

    try {
      const preflight = await preflightLoanDataExport(loanIds, template.id);
      if (!mountedRef.current) {
        return;
      }
      const excludedIds = new Set(preflight.excludedLoans.map((loan) => loan.loanId));
      const exportIds = loanIds.filter((loanId) => !excludedIds.has(loanId));
      if (excludedIds.size > 0) {
        excludedIds.forEach((loanId) => removeLoanDataExportCandidate(loanId));
        toast.warning(`${pluralized(excludedIds.size, 'loan')} left out of the Excel`, {
          description: describeExcluded(preflight.excludedLoans)
        });
      }
      if (exportIds.length === 0) {
        toast.error('None of the selected loans can be exported');
        return;
      }

      const generated = await generateForPayload(template.id, exportIds);
      if (!mountedRef.current) {
        return;
      }
      setHistoryRefreshKey((current) => current + 1);
      await startDownload(generated.export, template.name);
    } catch (error) {
      if (mountedRef.current) {
        toast.error(errorText(error, 'The Excel could not be generated'));
      }
    } finally {
      if (operationRef.current === token) {
        operationRef.current = null;
        if (mountedRef.current) {
          setGenerating(false);
        }
      }
    }
  };

  const canGenerate = !generating
    && !catalogLoading
    && Boolean(selectedTemplate)
    && readySelected.length > 0
    && pendingCount === 0;

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-7xl flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="font-serif text-2xl font-medium text-primary">Data Exports</h1>
        <p className="text-sm text-muted-foreground">
          Excel data tapes from Encompass loans. Exports never change a loan.
        </p>
      </header>

      <Tabs defaultValue="create" className="flex min-w-0 flex-col gap-5">
        <TabsList className="self-start">
          <TabsTrigger value="create">Create export</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="mt-0 flex min-w-0 flex-col gap-5">
          <LoanSelectionPanel
            selected={readySelected}
            ownerKey={ownerKey}
            filterBarLeading={(
              <TemplatePicker
                templates={templates}
                selectedTemplateId={selectedTemplateId}
                onSelect={setSelectedTemplateId}
                loading={catalogLoading}
              />
            )}
            toolbarActions={(
              <>
                <SelectedLoansMenu selected={readySelected} pendingCount={pendingCount} />
                <Button
                  type="button"
                  data-preserve-disabled
                  disabled={!canGenerate}
                  onClick={() => void handleGenerate()}
                >
                  {generating ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />}
                  {generating ? 'Generating…' : 'Generate Excel'}
                </Button>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-0 min-w-0">
          <ExportHistoryTable refreshKey={historyRefreshKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SelectedLoansMenu({
  selected,
  pendingCount
}: {
  selected: LoanDataExportCandidate[];
  pendingCount: number;
}) {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const total = selected.length + pendingCount;
  const totalVolume = selected.reduce((sum, candidate) => sum + (candidate.loanAmount ?? 0), 0);
  const label = total === 0
    ? 'No loans selected'
    : pendingCount > 0 && selected.length === 0
      ? `Checking ${pluralized(pendingCount, 'loan')}…`
      : `${pluralized(total, 'loan')} selected`;

  // Closing the menu hands focus back to its button, which always stays on
  // screen; removing one loan moves focus to its neighbour in the list.
  const clearSelection = () => {
    clearLoanDataExportCandidates();
    setOpen(false);
  };

  const removeLoan = (index: number) => {
    const neighbour = selected[index + 1] ?? selected[index - 1];
    if (!neighbour) {
      removeLoanDataExportCandidate(selected[index].id);
      if (pendingCount === 0) {
        setOpen(false);
      }
      return;
    }
    listRef.current?.querySelector<HTMLButtonElement>(`[data-loan-id="${neighbour.id}"]`)?.focus();
    removeLoanDataExportCandidate(selected[index].id);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline">
          {pendingCount > 0 ? <Loader2 className="animate-spin" /> : null}
          {label}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={8} className="w-80 p-0">
        {total === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            Check loans in the table, or use Select all matching.
          </p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 border-b px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold">{pluralized(total, 'loan')} selected</p>
                <p className="text-xs text-muted-foreground">
                  {pendingCount > 0
                    ? `Checking access to ${pluralized(pendingCount, 'loan')}`
                    : `${formatCurrency(totalVolume)} total loan amount`}
                </p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
                Clear selection
              </Button>
            </div>
            {selected.length > 0 ? (
              <ul ref={listRef} className="popover-scroll max-h-72 overflow-y-auto p-1.5" aria-label="Selected loans">
                {selected.map((candidate, index) => (
                  <li key={candidate.id} className="flex items-center gap-2 rounded-md px-2 py-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium tabular-nums">{candidate.loanNumber || 'Loan'}</p>
                      <p className="truncate text-xs text-muted-foreground">{borrowerName(candidate)}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      data-loan-id={candidate.id}
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${candidate.loanNumber || 'loan'}`}
                      onClick={() => removeLoan(index)}
                    >
                      <X />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
